import { clearCachedBranding } from '../library/brandingCache';
import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { AcceptInvitationRequest, IInvitation } from "../library/models/invitation";
import { AuthResponse, ChangePasswordRequest, ForgotPasswordRequest, UserLoginRequest, UserRegistrationRequest } from "../types";
import { BaseService } from "./baseService";

export class AuthService extends BaseService {
    loginMutation(): UseMutationOptions<AuthResponse, Error, UserLoginRequest> {
        return this.createMutation<AuthResponse, UserLoginRequest>("/auth/login");
    }

    registerMutation(): UseMutationOptions<AuthResponse, Error, UserRegistrationRequest> {
        return this.createMutation<AuthResponse, UserRegistrationRequest>("/auth/register");
    }

    setAuthToken(token: string): void {
        localStorage.setItem('authToken', token);
    }

    removeAuthToken(): void {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        // The signed-out shell is Carefull's, so the clinic's colours go with the session.
        clearCachedBranding();
    }

    /**
     * A session as one of the clinic's therapists, for its administrator. Hand-written because
     * the therapist's id goes in the path. The answer is taken up by `enterImpersonation`,
     * never by `adoptSession`: it belongs to this tab alone.
     */
    impersonateMutation(): UseMutationOptions<AuthResponse, Error, number> {
        return {
            mutationFn: (therapistId: number): Promise<AuthResponse> =>
                this.post<AuthResponse>(`/auth/impersonation/${therapistId}`),
        };
    }

    forgotPasswordMutation(): UseMutationOptions<void, Error, ForgotPasswordRequest> {
        return this.createMutation<void, ForgotPasswordRequest>("/users/change-password", 'put');
    }

    verifyChangePasswordCode(code: string | undefined): UseQueryOptions<boolean, Error> {
        return this.createGetQuery(`users/change-password/${code}`);
    }
    
    changePasswordMutation(code: string): UseMutationOptions<void, Error, ChangePasswordRequest> {
        return this.createMutation<void, ChangePasswordRequest>(`users/change-password/${code}`);
    }

    /**
     * Reads an invitation without spending it, so the accept screen can name the clinic
     * and decide whether to ask for a password before the recipient commits.
     *
     * A failure means the link is no good -- the API answers 400 for unknown, expired,
     * withdrawn and malformed codes alike. A code that was accepted is not a failure: it
     * answers 200 with `status: 'ALREADY_ACCEPTED'` and nothing else.
     */
    describeInvitation(code: string | undefined): UseQueryOptions<IInvitation, Error> {
        return this.createGetQuery<IInvitation>(`/users/activate/${code}`);
    }

    /**
     * Spends the invitation. The variable is `undefined` when the account already has a
     * password: the body is optional, and sending a password the API did not ask for is
     * refused rather than ignored, so that an invitation can never reset one.
     */
    acceptInvitationMutation(
        code: string | undefined
    ): UseMutationOptions<void, Error, AcceptInvitationRequest | undefined> {
        return this.createMutation<void, AcceptInvitationRequest | undefined>(
            `/users/activate/${code}`
        );
    }
}